import { FC, useState } from 'react';
import clsx from 'clsx';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import {
  Grid,
  Button,
  Divider,
  Typography,
  Slider,
  TextField,
  InputAdornment
} from '@mui/material';
import { FilterList, Close, ArrowDropDown, ArrowDropUp, Search } from '@mui/icons-material';
import { NeighborhoodDropdown, TerritoryDropdown } from '../../../components/dropdowns';

interface IFramerFilters {
  isLoading: boolean;
  applyFilters: (clearFilters?: boolean) => void;
  setPage: (val: number) => void;
  setHasAppliedFilters: (val: boolean) => void;
  hasAppliedFilters?: boolean;

  setBusinessNameSearchValue: (val: string) => void;
  businessNameSearchValue: string;
  handleBusinessNameSearch: (clearSearch?: boolean) => void;

  setContactNameSearchValue: (val: string) => void;
  contactNameSearchValue: string;
  handleContactNameSearch: (clearSearch?: boolean) => void;

  territories: { territory: string }[];
  setSelectedTerritoryId?: (val: string | number) => void;
  selectedTerritoryId: string | number;

  neighborhoods: { neighborhood: string }[];
  setSelectedNeighborhoodId: (val: string | number) => void;
  selectedNeighborhoodId: string | number;

  activeBuildCounts: { activeBuildCount: number }[];
  setSelectedActiveBuildCount: (val: number) => void;
  selectedActiveBuildCount: number;
}

export const FramerFilters: FC<IFramerFilters> = ({
  isLoading,
  applyFilters,
  businessNameSearchValue,
  setBusinessNameSearchValue,
  handleBusinessNameSearch,
  contactNameSearchValue,
  setContactNameSearchValue,
  handleContactNameSearch,
  selectedTerritoryId,
  setSelectedTerritoryId,
  selectedNeighborhoodId,
  setSelectedNeighborhoodId,
  activeBuildCounts,
  selectedActiveBuildCount,
  setSelectedActiveBuildCount
}) => {
  const [hasAppliedFilters, setHasAppliedFilters] = useState<boolean>(false);
  const [isMobileFilterButtonOpen, toggleMobileFilter] = useState<boolean>(false);
  const classes = useStyles({ isMobileFilterButtonOpen });

  // Used to help determine when to toggle hasAppliedFilters on/off for search specific functions
  const hasSelectedFilters = () => {
    return (
      !!businessNameSearchValue ||
      !!selectedTerritoryId ||
      !!selectedNeighborhoodId ||
      !!contactNameSearchValue ||
      !!selectedActiveBuildCount
    );
  };

  return (
    <>
      <Button
        color="secondary"
        variant="contained"
        className={classes.mobileButton}
        startIcon={<FilterList />}
        endIcon={isMobileFilterButtonOpen ? <ArrowDropUp /> : <ArrowDropDown />}
        onClick={() => {
          toggleMobileFilter(!isMobileFilterButtonOpen);
        }}
      >
        Filters
      </Button>
      {!isMobileFilterButtonOpen && <Divider className={classes.divider} />}
      <Grid container spacing={1} alignItems="center" className={classes.wrapper}>
        <Grid item xs={12} sm={4}>
          <TextField
            size="small"
            fullWidth
            variant="outlined"
            placeholder="Search Business Name..."
            name="businessNameSearch"
            value={businessNameSearchValue}
            disabled={isLoading}
            InputProps={{
              startAdornment: (
                <InputAdornment
                  position="start"
                  className={classes.searchIcon}
                  onClick={() => {
                    if (businessNameSearchValue.length > 0) {
                      setHasAppliedFilters(true);
                      handleBusinessNameSearch();
                    } else if (!hasSelectedFilters()) {
                      setHasAppliedFilters(false);
                    }
                  }}
                >
                  <Search />
                </InputAdornment>
              ),
              endAdornment: businessNameSearchValue ? (
                <InputAdornment
                  position="end"
                  className={classes.searchIcon}
                  onClick={() => {
                    setBusinessNameSearchValue('');
                    handleBusinessNameSearch(true);
                    if (!contactNameSearchValue) {
                      setHasAppliedFilters(false);
                    }
                  }}
                >
                  <Close />
                </InputAdornment>
              ) : null
            }}
            onKeyDown={e => {
              if (e.key === 'Enter' && businessNameSearchValue.length > 0) {
                setHasAppliedFilters(true);
                handleBusinessNameSearch();
              }
            }}
            onChange={e => {
              setBusinessNameSearchValue(e.target.value);
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            size="small"
            fullWidth
            variant="outlined"
            placeholder="Search Contact Name..."
            name="contactNameSearch"
            value={contactNameSearchValue}
            disabled={isLoading}
            InputProps={{
              startAdornment: (
                <InputAdornment
                  position="start"
                  className={classes.searchIcon}
                  onClick={() => {
                    if (contactNameSearchValue.length > 0) {
                      setHasAppliedFilters(true);
                      handleContactNameSearch();
                    } else if (!hasSelectedFilters()) {
                      setHasAppliedFilters(false);
                    }
                  }}
                >
                  <Search />
                </InputAdornment>
              ),
              endAdornment: contactNameSearchValue ? (
                <InputAdornment
                  position="end"
                  className={classes.searchIcon}
                  onClick={() => {
                    setContactNameSearchValue('');
                    handleContactNameSearch(true);
                    if (!businessNameSearchValue) {
                      setHasAppliedFilters(false);
                    }
                  }}
                >
                  <Close />
                </InputAdornment>
              ) : null
            }}
            onKeyDown={e => {
              if (e.key === 'Enter' && contactNameSearchValue.length > 0) {
                setHasAppliedFilters(true);
                handleContactNameSearch();
              }
            }}
            onChange={e => {
              setContactNameSearchValue(e.target.value);
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TerritoryDropdown
            isRequired={false}
            isDisabled={isLoading}
            value={selectedTerritoryId}
            handleClear={() => {
              setSelectedTerritoryId('');
            }}
            handleChange={val => {
              setSelectedTerritoryId(val);
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <NeighborhoodDropdown
            isRequired={false}
            isDisabled={isLoading}
            value={selectedNeighborhoodId}
            handleChange={val => {
              setSelectedNeighborhoodId(val);
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <Typography>Active Build Count</Typography>
          <Slider
            value={selectedActiveBuildCount}
            onChange={(_, val) => {
              setSelectedActiveBuildCount(val as number);
            }}
            size={'small'}
            valueLabelDisplay="auto"
            max={Math.max(...activeBuildCounts.map(o => o.activeBuildCount))}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Button
            color="primary"
            variant="contained"
            className={classes.button}
            disabled={isLoading}
            startIcon={<FilterList />}
            onClick={() => {
              setHasAppliedFilters(true);
              applyFilters();
            }}
          >
            Apply Filters
          </Button>
          {hasAppliedFilters && (
            <Button
              className={clsx(classes.button, classes.resetButton)}
              variant="contained"
              disabled={isLoading}
              startIcon={<Close />}
              color="inherit"
              onClick={() => {
                setHasAppliedFilters(false);
                setBusinessNameSearchValue('');
                setContactNameSearchValue('');
                setSelectedTerritoryId('');
                setSelectedNeighborhoodId('');
                setSelectedActiveBuildCount(Number());
                applyFilters(true);
              }}
            >
              Reset
            </Button>
          )}
        </Grid>
      </Grid>
    </>
  );
};

const useStyles = makeStyles<Theme, { isMobileFilterButtonOpen?: boolean }>((theme: Theme) => ({
  mobileButton: {
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      display: 'none'
    }
  },
  button: {
    marginTop: 4,
    marginBottom: 4,
    height: 40,
    textTransform: 'capitalize',
    width: '100%',
    '@media (min-width: 400px)': {
      width: '48%'
    },
    [theme.breakpoints.up('sm')]: {
      width: 'auto'
    }
  },
  resetButton: {
    '@media (min-width: 400px)': {
      marginLeft: 11
    },
    [theme.breakpoints.up('sm')]: {
      marginLeft: theme.spacing(1)
    }
  },
  wrapper: ({ isMobileFilterButtonOpen }) =>
    isMobileFilterButtonOpen
      ? {
          marginTop: 10,
          marginBottom: 10,
          display: 'flex'
        }
      : {
          display: 'none',
          marginBottom: theme.spacing(1),
          [theme.breakpoints.up('sm')]: {
            display: 'flex',
            marginBottom: theme.spacing(1),
            marginTop: theme.spacing(0.5)
          }
        },
  divider: {
    display: 'block',
    marginBottom: theme.spacing(1),
    [theme.breakpoints.up('sm')]: {
      display: 'none'
    }
  },
  searchIcon: {
    cursor: 'pointer',
    color: theme.palette.grey[500]
  },
  ellipsis: {
    fontSize: 16,
    width: 'auto',
    whiteSpace: 'normal',
    overflow: 'visible',
    '@media (min-width: 960px)': {
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflow: 'hidden'
    },
    '@media (min-width: 1400px)': {
      width: 'auto',
      whiteSpace: 'normal',
      overflow: 'visible'
    }
  }
}));
